import * as Yup from 'yup';

/** 📦 Schema de arquivos de imagem */
export const imageFileSchema = Yup.mixed()
  .test(
    'fileFormat',
    'Apenas arquivos .png, .jpg e .jpeg são aceitos',
    (value) => {
      if (value && value instanceof File) {
        const supportedFormats = ['image/png', 'image/jpg', 'image/jpeg'];
        return supportedFormats.includes(value.type);
      }
      return true;
    },
  )
  .test(
    'fileMaxSize',
    'Apenas arquivos menores que 5 MB são aceitos',
    (value) => {
      if (value && value instanceof File) {
        const maxSizeInBytes = 5 * 1024 * 1024;
        return value.size <= maxSizeInBytes;
      }
      return true;
    },
  )
  .notRequired();

/** 📦 Schema de arquivos de documentos */
export const docFileSchema = Yup.mixed()
  .test('fileFormat', 'Apenas arquivos .doc, .docx são aceitos', (value) => {
    if (value && value instanceof File) {
      const supportedFormats = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      return supportedFormats.includes(value.type);
    }
    return true;
  })
  .test(
    'fileMaxSize',
    'Apenas arquivos menores que 5 MB são aceitos',
    (value) => {
      if (value && value instanceof File) {
        const maxSizeInBytes = 5 * 1024 * 1024;
        return value.size <= maxSizeInBytes;
      }
      return true;
    },
  )
  .notRequired();

/** 📦 Schema de objeto Label/Value */
export const labelValueSchema = Yup.object().shape({
  label: Yup.string().nullable(),
  value: Yup.number().nullable(),
});

/** 📦 Schema de endereço */
export const addressSchema = Yup.object()
  .shape({
    street: Yup.string().required('Campo obrigatório'),
    number: Yup.string().required('Campo obrigatório'),
    neighborhood: Yup.string().required('Campo obrigatório'),
    complement: Yup.string().nullable(),
    city: labelValueSchema.required('Campo obrigatório'),
    zipCode: Yup.string().required('Campo obrigatório'),
  })
  .nullable();

/** 📦 Schema de representante */
export const representativeSchema = Yup.object().shape({
  name: Yup.string().nullable(),
  role: Yup.string().nullable(),
});

/** 📦 Schema de suporte */
export const supportSchema = Yup.object().shape({
  phoneNumber: Yup.string().nullable(),
  contactName: Yup.string().nullable(),
});

/** 📦 Schema de TCE */
export const tceSchema = Yup.object().shape({
  insuranceCompanyId: labelValueSchema.nullable(),
  policy: Yup.string().nullable(),
  rescissionTermModel: docFileSchema,
});

const DD_MM_FORMAT_REGEX = /^\d{2}\/\d{2}$/;

const parseDdMmToDayOfYear = (value?: string | null): number | null => {
  if (!value || !DD_MM_FORMAT_REGEX.test(value)) {
    return null;
  }

  const [day, month] = value.split('/').map(Number);
  const date = new Date(2000, month - 1, day);

  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  const startOfYear = new Date(2000, 0, 1);
  const diffMs = date.getTime() - startOfYear.getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
};

const schoolDayMonthSchema = Yup.string()
  .nullable()
  .transform((value) => {
    if (!value) return null;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  })
  .test('dd-mm-format', 'Formato inválido. Use dia/mês (DD/MM).', (value) => {
    if (!value) return true;
    return DD_MM_FORMAT_REGEX.test(value);
  })
  .test('dd-mm-valid-date', 'Dia/mês inválido.', (value) => {
    if (!value) return true;
    return parseDdMmToDayOfYear(value) !== null;
  });

export const periodRangeSchema = Yup.object()
  .shape({
    startDate: schoolDayMonthSchema,
    endDate: schoolDayMonthSchema,
  })
  .test(
    'period-filled-consistency',
    'Preencha início e término do período.',
    (period) => {
      if (!period) return true;
      const hasStart = !!period.startDate;
      const hasEnd = !!period.endDate;
      return (hasStart && hasEnd) || (!hasStart && !hasEnd);
    },
  )
  .test(
    'start-before-end',
    'A data de início deve ser menor ou igual à data de término.',
    (period) => {
      if (!period?.startDate || !period?.endDate) return true;
      const start = parseDdMmToDayOfYear(period.startDate);
      const end = parseDdMmToDayOfYear(period.endDate);
      if (start === null || end === null) return true;
      return start <= end;
    },
  );

export const schoolPeriodSchema = Yup.object()
  .shape({
    firstPeriod: periodRangeSchema,
    secondPeriod: periodRangeSchema,
  })
  .test(
    'no-period-collision',
    'Os períodos letivos não podem se sobrepor.',
    (value) => {
      const firstStart = value?.firstPeriod?.startDate;
      const firstEnd = value?.firstPeriod?.endDate;
      const secondStart = value?.secondPeriod?.startDate;
      const secondEnd = value?.secondPeriod?.endDate;

      if (!firstStart || !firstEnd || !secondStart || !secondEnd) {
        return true;
      }

      const fStart = parseDdMmToDayOfYear(firstStart);
      const fEnd = parseDdMmToDayOfYear(firstEnd);
      const sStart = parseDdMmToDayOfYear(secondStart);
      const sEnd = parseDdMmToDayOfYear(secondEnd);

      if (
        fStart === null ||
        fEnd === null ||
        sStart === null ||
        sEnd === null
      ) {
        return true;
      }

      return fEnd < sStart || sEnd < fStart;
    },
  );

/** 📦 Schema de Settings */
export const settingSchema = Yup.object().shape({
  appStudentBlockActivityScheduling: Yup.boolean().nullable(),
  appStudentBlockQrCodeActivityStart: Yup.boolean().nullable(),
  toleratedDistanceFromInternshipLocation:
    Yup.number().required('Campo obrigatório'),
  toleranceStartMinutes: Yup.number().required('Campo obrigatório'),
  toleranceEndMinutes: Yup.number().required('Campo obrigatório'),
  appStudentHideTotalWorkload: Yup.boolean().nullable(),
  showFinalActivityEvaluation: Yup.boolean().nullable(),
  finalActivityEvaluationRequired: Yup.boolean().nullable(),
  appStudentHideWorkloadValidated: Yup.boolean().nullable(),
  notifyWppTeacherPendingActivity: Yup.boolean().nullable(),
  appStudentStartOnlyToleratedDistance: Yup.boolean().nullable(),
  appStudentFinishOnlyToleratedDistance: Yup.boolean().nullable(),
  appProfessionalShowFeedbackValidation: Yup.boolean().nullable(),
  appProfessionalShowDailyWorkloadByStudentReport: Yup.boolean().nullable(),
  appProfessionalSubtaskRequiredByDefault: Yup.boolean().nullable(),
  appProfessionalShowFaultInValidation: Yup.boolean().nullable(),
  appStudentEvaluationField: Yup.boolean().nullable(),
  appStudentAllowGenerateQrcodeEvaluation: Yup.boolean().nullable(),
  appStudentDemandStudentPhotoOnActivityStart: Yup.boolean().nullable(),
  appStudentDemandStudentPhotoOnActivityEnd: Yup.boolean().nullable(),
  appStudentConsiderAutoFinishedActivityAsMissed: Yup.boolean().nullable(),
  appStudentConsiderActivityWithLocationBlockAsMissed: Yup.boolean().nullable(),
  partners: Yup.object().nullable(),
  defaultEvaluationDisplayable: Yup.boolean().nullable(),
  appStudentAllowReschedulingMissedActivities: Yup.boolean().nullable(),
  allowExternalProfessionalActivityValidation: Yup.boolean().nullable(),
  appStudentDemandFileOnActivityJustification: Yup.boolean().nullable(),
  appStudentAllowEvaluationAcceptanceByStudent: Yup.boolean().nullable(),
  sso: Yup.object().shape({
    enabled: Yup.boolean().nullable(),
    secret: Yup.string().when('enabled', {
      is: true,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    clientId: Yup.string().when('enabled', {
      is: true,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    tenantId: Yup.string().when('enabled', {
      is: true,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    domain: Yup.string().when('enabled', {
      is: true,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    url: Yup.string().when('enabled', {
      is: true,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
  }),
});

// Schema para CNPJ com validação customizada
export const cnpjSchema = Yup.string()
  .cpfOrCnpjType('CNPJ inválido')
  .notRequired()
  .required('Campo obrigatório');

// String obrigatória
export const requiredStringSchema = Yup.string().required('Campo obrigatório');

// String opcional
export const optionalStringSchema = Yup.string().nullable();
